import adminApiClient from '../../api/admin-api-client'
import { BaseCollection } from '../BaseCollection'
import { Subdivision } from './Subdivision'
import { EventBus } from '../../util/EventBus'
import { GridStateManager } from '../../util/GridStateManager'

/**
 * Class SubdivisionCollection
 *
 * @extends BaseCollection
 */
export class SubdivisionCollection extends BaseCollection {
  constructor(props) {
    super(props)
    this.companyId = props.companyId || 0
    this.userId = props.userId || 0
    this.collectionKey = 'subdivisionId'
    this.stateManager = new GridStateManager({
      gridName: 'subdivisionGrid',
      collection: this,
      autoInitialize: false,
    })
  }

  setItems(data) {
    this.items = data.map((sd) => new Subdivision(sd))
  }

  /**
   * GET /api/Companies/{companyId}/querysubdivisions
   * @param {number} companyId
   * @returns
   */
  async fetch() {
    if (!this.companyId) {
      throw new Error('Company ID missing!')
    }

    this.isLoading = true
    this.stateManager.beforeFetch()
    try {
      const sdResponse = await adminApiClient.getSubdivisions(this.companyId, {
        Page: this.page,
        PageSize: this.pageSize,
        OrderBy: this.orderBy,
        Filter: this.filter,
      })
      this.loadCount++
      this.count = sdResponse.data.count
      this.items = sdResponse.data.data.map((sd) => new Subdivision(sd))
      this.isLoading = false
      this.stateManager.afterFetch()
      if (this.filter) {
        EventBus.$emit('restoreFocusEvent')
      }
      return this.items
    } catch (ex) {
      this.isLoading = false
      this.stateManager.handleFetchError(ex)
      throw ex
    }
  }
  /**
   * GET /api/Companies/{companyId}/querysubdivisions/{userDbId}
   *
   * @param {number} companyId
   * @param {number} userId
   * @returns
   */
  async fetchByUserAndCompany() {
    if (!this.companyId || !this.userId) {
      throw new Error('Company ID or user id missing!')
    }
    this.isLoading = true
    try {
      const sdResponse = await adminApiClient.getUserSubdivisions(
        this.companyId,
        this.userId,
        this.page,
        this.pageSize || 20,
      )

      if (this.page === 1) {
        this.items = sdResponse.data.data.map((sd) => new Subdivision(sd))
      } else {
        this.items = [...this.items, ...sdResponse.data.data.map((sd) => new Subdivision(sd))]
      }

      this.count = sdResponse.data.count
      this.loadCount++
      this.isLoading = false
      return this.items
    } catch (ex) {
      this.isLoading = false
      throw ex
    }
  }

  async fetchById(subdivisionId) {
    this.isLoading = true
    const entityContactsResponse = await adminApiClient.getSubdivisionEntityContacts(this.companyId, subdivisionId)
    const entityAddressesResponse = await adminApiClient.getSubdivisionEntityAddresses(this.companyId, subdivisionId)

    // Map entity contacts to their respective fields
    const entityContact = {}
    if (entityContactsResponse?.data) {
      entityContactsResponse.data.forEach((contact) => {
        switch (contact.contactTypeId) {
          case 1:
            entityContact.phone = contact.contact
            break
          case 2:
            entityContact.commercialPhone = contact.contact
            break
          case 3:
            entityContact.email = contact.contact
            break
          case 4:
            entityContact.invoicingEmail = contact.contact
            break
          case 5:
            entityContact.trainingEmail = contact.contact
            break
        }
      })
    }

    // Map entity addresses
    const entityAddress = {}
    const addressTypeIdToString = {
      1: 'primaryAddress',
      2: 'secondaryAddress',
      3: 'invoicingAddress',
    }

    entityAddressesResponse.data.forEach((address) => {
      const addressTypeString = addressTypeIdToString[address.addressTypeId]
      if (addressTypeString) {
        entityAddress[addressTypeString] = {
          countryCodeId: address.countryCodeId,
          countryCode: address.countryCode,
          countryText: address.countryText,
          addressLine1: address.addressLine1,
          addressLine2: address.addressLine2,
          city: address.city,
          stateProvince: address.stateProvince,
          postCode: address.postCode,
        }
      }
    })

    try {
      const sdResponse = await adminApiClient.getSubdivision(this.companyId, subdivisionId)
      // Merge the entityContact and entityAddress data with the subdivision data
      const subdivisionData = {
        ...sdResponse.data,
        companyId: this.companyId,
        entityContact,
        entityAddress,
      }
      this.items = [new Subdivision(subdivisionData)]
      this.loadCount++
      this.isLoading = false
      return this.items
    } catch (ex) {
      this.isLoading = false
      throw ex
    }
  }

  /**
   * @override
   *
   * POST /api/companies/{companyId}/subdivision
   *
   * @param {Subdivision} subdivision
   * @param {int} companyId
   */
  async create(subdivision) {
    if (!this.companyId) {
      throw new Error('CompanyId is not set')
    }

    const data = {
      companyId: this.companyId,
      data: {
        subdivisionName: subdivision.subdivisionName,
        externalCompanyId: subdivision.externalCompanyId, // the GUID
        externalCompanyNumber: subdivision.externalCompanyNumber, // ERP company Number
        endpointId: subdivision.endpointId,
        fiscalId: subdivision.fiscalId,
        locale: subdivision.locale,
      },
    }
    this.isLoading = true

    subdivision.isLoading = true
    return adminApiClient
      .createSubdivision(this.companyId, data)
      .then(async (response) => {
        if (response.status === 200) {
          const subdivisionId = response.data

          // Entity Contact type mapping
          const contactTypes = {
            phone: { id: 1, value: subdivision.entityContact?.phone },
            commercialPhone: { id: 2, value: subdivision.entityContact?.commercialPhone },
            email: { id: 3, value: subdivision.entityContact?.email },
            invoicingEmail: { id: 4, value: subdivision.entityContact?.invoicingEmail },
            trainingEmail: { id: 5, value: subdivision.entityContact?.trainingEmail },
          }

          // Helper function to create contact
          const createContact = async (contactTypeId, contactValue) => {
            if (contactValue) {
              const contactData = {
                contactTypeId,
                entityTypeId: 2,
                entityId: subdivisionId,
                contact: contactValue,
              }
              await adminApiClient.createEntityContact(contactData)
            }
          }

          // Create all contacts
          await Promise.all(Object.values(contactTypes).map(({ id, value }) => createContact(id, value)))

          // Create entity addresses
          if (subdivision.entityAddress) {
            // Address type mapping
            const addressTypeMap = {
              primaryAddress: 1,
              secondaryAddress: 2,
              invoicingAddress: 3,
            }

            for (const [addressTypeId, address] of Object.entries(subdivision.entityAddress)) {
              const addressData = {
                entityTypeId: 2,
                entityId: subdivisionId,
                addressTypeId: addressTypeMap[addressTypeId],
                ...address,
              }
              await adminApiClient.createEntityAddress(addressData)
            }
          }

          return adminApiClient.getSubdivision(this.companyId, subdivisionId)
        } else {
          throw new Error('Failed to save subdivision')
        }
      })
      .then((response) => {
        if (response.status === 200) {
          const newSubdivision = new Subdivision({ companyId: this.companyId, ...response.data })
          this.items.push(newSubdivision)
          this.added = newSubdivision
          this.isLoading = false
          subdivision.isLoading = false
          return newSubdivision
        } else {
          throw new Error('Failed to save subdivision')
        }
      })
      .catch((ex) => {
        this.isLoading = false
        subdivision.isLoading = false
        throw ex
      })
  }

  /**
   * @override
   *
   * DELETE /api/companies/{company}/subdivision/{subdivisionId}
   *
   */
  delete(subdivisionId, companyId) {
    let sd = this.items.find((sd) => sd.subdivisionId === subdivisionId)
    if (!sd) {
      throw new Error('Subdivision not found')
    }

    sd.isLoading = true

    return (async () => {
      try {
        // Get all entity contacts for the subdivision
        const entityContactsResponse = await adminApiClient.getSubdivisionEntityContacts(companyId, subdivisionId)
        // Delete all entity contacts
        await Promise.all(
          entityContactsResponse.data.map((contact) => adminApiClient.deleteEntityContact(contact.contactId)),
        )

        // Get all entity addresses for the subdivision
        const entityAddressesResponse = await adminApiClient.getSubdivisionEntityAddresses(companyId, subdivisionId)
        // Delete all entity addresses
        await Promise.all(
          entityAddressesResponse.data.map((address) => adminApiClient.deleteEntityAddress(address.addressId)),
        )
        // Delete the subdivision
        const response = await adminApiClient.deleteSubdivision(companyId, subdivisionId)

        sd.isLoading = false
        if (response.status === 200) {
          let stillInArray = this.items.findIndex((sd) => sd.subdivisionId === subdivisionId)
          if (stillInArray > -1) {
            this.items.splice(stillInArray, 1)
          }
          this.removed = sd
          return true
        }
        throw new Error('Failed to delete')
      } catch (ex) {
        sd.isLoading = false
        throw ex
      }
    })()
  }

  /**
   * Removes user to subdivsion connection
   *
   * Note: same call is used in UserCollection
   *
   * POST /api/Users/removesubdivision
   * @param {*} userId
   * @param {*} subdivisionId
   * @returns
   */
  async removeUserSubdivisionConnection(userDbId, companyId, subdivisionId) {
    let subdivision = this.items.find((sd) => sd.subdivisionId === subdivisionId)
    subdivision.isLoading = true
    this.isLoading = true
    try {
      let response = await adminApiClient.removeUserFromSubdivision({
        userId: userDbId,
        companyId: companyId,
        SubdivisionId: subdivisionId,
        externalCompanyNumber: null,
      })
      if (response.status === 204) {
        subdivision.isLoading = false
        this.isLoading = false
        let stillInArray = this.items.findIndex((sd) => sd.subdivisionId === subdivisionId)
        if (stillInArray > -1) {
          this.items.splice(stillInArray, 1)
        }
        return true
      }
      throw new Error('Failed to remove')
    } catch (ex) {
      this.isLoading = false
      throw ex
    }
  }

  /**
   * Connects user to subdivision
   *
   * POST /api/Users/addorupdatesubdivision
   *
   * @param {*} connectionProps
   */
  async connectUserToSubdivision(connectionProps) {
    const params = {
      userId: connectionProps.user.userDbId,
      companyId: connectionProps.user.companyId,
      groupId: null,
      externalCompanyUser: connectionProps.externalCompanyUser,
      endpointId: connectionProps.endpointId !== 0 ? connectionProps.endpointId : null,
      fiscalId: connectionProps.fiscalId,
      locale: connectionProps.locale,
      subdivisionDefinition: {
        subdivisionId: connectionProps.subdivision.subdivisionId,
        externalCompanyNumber: null,
        externalCompanyId: null,
      },
    }
    connectionProps.subdivision.isLoading = true
    this.isLoading = true
    try {
      const response = await adminApiClient.connectUserToSubdivision(params)
      if (response.status === 200) {
        this.isLoading = false
        connectionProps.subdivision.isLoading = false
        this.items.push(connectionProps.subdivision)
        return true
      } else {
        throw new Error('Failed to add connection')
      }
    } catch (ex) {
      connectionProps.subdivision.isLoading = false
      this.isLoading = false
      throw ex
    }
  }
}
